// 261030 S5c — «Gộp đạo cụ trùng (N)»: kế hoạch gộp do BE tính sẵn (`step1.pendingAutoMerges` + `pendingAutoMergesHash`, $0, không AI).
// THUẦN (không antd). Hợp đồng: `s-fe-contract.md` mục «S5c». Không hiện tiền.
import { PLAN_BUSY_TEXT, isPlanBusy } from './planBusy';

export interface PendingMerge { findingId: string; fromId: string; toId: string; fromName: string; toName: string; movedEventIds: string[] }
export interface MergePlan { items: PendingMerge[]; hash: string; n: number }
type StatusLike = { step1?: { hash?: string; pendingAutoMerges?: PendingMerge[] | null; pendingAutoMergesHash?: string | null } | null; gates?: { STEP1?: { verdict?: string } | null } | null } | null | undefined;

const cleanItem = (x: any): PendingMerge | null => (x && typeof x.findingId === 'string' && typeof x.fromId === 'string' && typeof x.toId === 'string'
  ? { findingId: x.findingId, fromId: x.fromId, toId: x.toId, fromName: String(x.fromName ?? x.fromId), toName: String(x.toName ?? x.toId), movedEventIds: Array.isArray(x.movedEventIds) ? x.movedEventIds.map(String) : [] } : null);
/** Kế hoạch gộp hiện hành. Vắng / rỗng / thiếu băm ⇒ null (không có nút: không có băm thì không khoá được đúng kế hoạch đã xem). */
export function mergePlanOf(source: { pendingAutoMerges?: unknown; pendingAutoMergesHash?: unknown } | null | undefined): MergePlan | null {
  const list = Array.isArray(source?.pendingAutoMerges) ? (source!.pendingAutoMerges as unknown[]).map(cleanItem).filter((x): x is PendingMerge => !!x) : [];
  const hash = typeof source?.pendingAutoMergesHash === 'string' ? source.pendingAutoMergesHash : '';
  return list.length && hash ? { items: list, hash, n: list.length } : null;
}
export const mergePlanOfStatus = (status: StatusLike): MergePlan | null => mergePlanOf(status?.step1);

export const mergeButtonLabelOf = (n: number): string => `Gộp đạo cụ trùng (${n})`;
/** «bottle → Aren's stone bottle (4 video)». */
export const mergeLineOf = (m: PendingMerge): string => `${m.fromName} → ${m.toName} (${m.movedEventIds.length ? `${m.movedEventIds.length} video chuyển sang` : 'không video nào phải chuyển'})`;

export interface MergeBanner { title: string; description: string; label: string }
export function mergeBannerOf(plan: MergePlan): MergeBanner {
  return {
    title: `Máy thấy ${plan.n} đạo cụ trùng nên có thể gộp`,
    description: `${plan.items.slice(0, 3).map(mergeLineOf).join(' · ')}${plan.n > 3 ? ` · và ${plan.n - 3} mục nữa` : ''}. Gộp xong không tốn tiền, không chạy AI.`,
    label: mergeButtonLabelOf(plan.n),
  };
}
export interface MergeBox { title: string; lines: string[]; notes: string[]; okText: string }
/** Hộp xác nhận chữ: danh sách `từ → đích`, cổng 1 sẽ cần duyệt lại (đã duyệt ⇒ thành «cũ»). */
export function mergeBoxOf(plan: MergePlan, gate1Ok: boolean): MergeBox {
  return {
    title: `Gộp ${plan.n} đạo cụ trùng?`,
    lines: plan.items.map(mergeLineOf),
    notes: [
      'Video của bản trần được gắn sang hàng đủ tên; bản trần không còn trong danh sách đạo cụ.',
      gate1Ok ? 'Cổng 1 đã duyệt sẽ thành «cũ» — bạn phải duyệt lại cổng 1 (và cổng 2, không có ảnh phải vẽ thêm).' : 'Cổng 1 sẽ đổi — đọc lại rồi duyệt.',
      'Gộp nhầm thì bấm «Khôi phục» ở từng mục. Không tốn tiền, không chạy AI.',
    ],
    okText: 'Gộp đạo cụ trùng',
  };
}

export interface MergeBody { baseHash: string; planHash: string; requestId: string }
export const mergeBodyOf = (a: { baseHash: string; planHash: string; requestId: string }): MergeBody => ({ baseHash: a.baseHash, planHash: a.planHash, requestId: a.requestId });
/** requestId giữ nguyên khi thử lại CÙNG (baseHash, planHash) (mất mạng / 504 — BE replay đúng kết quả lần đầu); kế hoạch/băm đổi ⇒ id mới. */
export type MergeRequestMemo = { key: string; requestId: string } | null;
export function mergeRequestIdOf(memo: MergeRequestMemo, a: { baseHash: string; planHash: string }, make: () => string): { requestId: string; memo: MergeRequestMemo } {
  const key = `${a.baseHash}|${a.planHash}`;
  if (memo && memo.key === key) return { requestId: memo.requestId, memo };
  const next = { key, requestId: make() };
  return { requestId: next.requestId, memo: next };
}

export function mergeOutcomeOf(response: any): { message: string; level: 'success' | 'info' } {
  const n = Array.isArray(response?.merged) ? response.merged.length : 0;
  return n
    ? { message: `Đã gộp ${n} đạo cụ trùng. Cổng 1 đã đổi — đọc lại rồi duyệt lại cổng 1 (và cổng 2).`, level: 'success' }
    : { message: 'Không còn đạo cụ trùng nào để gộp — đã tải lại.', level: 'info' };
}

export type MergeErrorView =
  | { kind: 'replan'; text: string; plan: MergePlan | null }   // AUTO_MERGE_PLAN_CHANGED: kế hoạch đổi ⇒ đóng hộp, tải lại, mở lại với kế hoạch mới (nếu còn)
  | { kind: 'reload'; text: string }                           // dữ liệu cũ ⇒ tải lại
  | { kind: 'busy'; text: string }                             // PLAN_BUSY ⇒ tải lại, bấm lại
  | { kind: 'plain'; text: string };                           // lỗi có câu riêng, không cần tải lại
const codeOf = (err: any): string => String(err?.response?.data?.code ?? err?.response?.data?.errorCode ?? '');
/** Câu + lối tiếp cho lỗi của route gộp. Mã lạ / WORKFLOW_RUN_ACTIVE ⇒ null (caller dùng `handleActiveJobs` / `apiErrorText`). */
export function mergeErrorViewOf(err: any): MergeErrorView | null {
  if (isPlanBusy(err)) return { kind: 'busy', text: PLAN_BUSY_TEXT };
  const code = codeOf(err);
  if (code === 'AUTO_MERGE_PLAN_CHANGED') {
    const extra = err?.response?.data?.extra;
    return { kind: 'replan', text: 'Kế hoạch gộp vừa đổi — đã tải lại, hãy xem danh sách mới rồi bấm lại.', plan: mergePlanOf({ pendingAutoMerges: extra?.pendingAutoMerges, pendingAutoMergesHash: extra?.currentPlanHash }) };
  }
  if (code === 'STEP1_HASH_MISMATCH') return { kind: 'reload', text: 'Bước ① vừa đổi sau khi bạn xem — đã tải lại, hãy xem rồi bấm lại.' };
  if (code === 'STEP1_NOT_READY') return { kind: 'plain', text: 'Bước ① chưa chạy xong — chưa gộp được.' };
  if (code === 'FLOW_NOT_SUPPORTED') return { kind: 'plain', text: 'Plan này dùng khung cũ — không gộp ở đây được.' };
  if (code === 'BAD_BODY') return { kind: 'reload', text: 'Yêu cầu không hợp lệ — đã tải lại, thử lại.' };
  return null;
}
